import { Tooltip } from "@xola/ui-kit";
import React from "react";
import { FieldType, FormResponse, PurchaseItem } from "types";
import { flipWeightUnit } from "../weightHelpers";
import { PhotoQuestionnaireCell } from "./PhotoQuestionnaireCell";
import { RowData } from "./types";
import { getFormattedPickupLocationTime } from "@/components/Questionnaire/PickupLocationSelectField";
import { formatFieldLabel, isAddonFieldResponse } from "@/models/Form/Field";

export const questionnaireCellValueGetter = ({ data }: { data: RowData }) => {
    if (!data || "balance" in data) {
        return null;
    }

    if (data.isOrganizer) {
        return data.item?.formResponse ?? null;
    }

    return data.guest?.formResponse ?? null;
};

interface QuestionnaireCellRendererProps {
    value: FormResponse | null;
    data: RowData;
    headerName: string;
    fieldType?: FieldType;
}

export const QuestionnaireCellRenderer = ({ value, data, headerName, fieldType }: QuestionnaireCellRendererProps) => {
    const isOrganizerRow = "isOrganizer" in data && data.isOrganizer;
    const isSummaryRow = "id" in data && data.id?.startsWith("summary");
    const isWeightColumn = headerName === "Weight";
    const isPickupColumn = headerName.trim().toLowerCase() === "pickup location";

    // Detect photo fields: prefer the type from the form definition (reliable),
    // fall back to the type on the form response field (may vary by API version).
    const questionnaireField = value?.fields?.find(
        (field) => field.label.trim().toLowerCase() === headerName.trim().toLowerCase(),
    );
    const isPhotoField = fieldType === FieldType.PHOTO || questionnaireField?.type === FieldType.PHOTO;

    // Render photo cell for photo fields
    if (isPhotoField) {
        return <PhotoQuestionnaireCell value={value} data={data} headerName={headerName} />;
    }

    const isEmptyValue = !value;

    if (isWeightColumn && isEmptyValue && (isOrganizerRow || isSummaryRow)) {
        const totalWeight = data.weight;
        if (!totalWeight) {
            return "";
        }
        const weightWithUnit = `${totalWeight.value} ${totalWeight.unit}`;
        const totalWeightInFlippedUnit = flipWeightUnit(totalWeight);
        const weightWithFlippedUnit = `${totalWeightInFlippedUnit.value} ${totalWeightInFlippedUnit.unit}`;
        const tooltipMessage = (
            <span>
                {weightWithUnit} | <span className="text-gray">{weightWithFlippedUnit}</span>
            </span>
        );
        return (
            <Tooltip content={tooltipMessage}>
                <span className="whitespace-nowrap">{weightWithUnit}</span>
            </Tooltip>
        );
    }

    if (isOrganizerRow && isPickupColumn) {
        return data.item?.pickupLocation ?? "";
    }

    if ("balance" in data || !value) {
        return null;
    }

    const questionnaireFieldAnswer = getQuestionnaireValue(value, headerName, data.item);

    if (!questionnaireFieldAnswer) return null;

    return (
        <Tooltip content={<span>{questionnaireFieldAnswer}</span>}>
            <span className="whitespace-nowrap">{questionnaireFieldAnswer}</span>
        </Tooltip>
    );
};

export const getQuestionnaireValue = (formResponse?: FormResponse, headerName?: string, item?: PurchaseItem) => {
    if (!formResponse || !headerName) {
        return null;
    }

    const questionnaireField = formResponse.fields.find(
        (field) => field.label.trim().toLowerCase() === headerName.trim().toLowerCase(),
    );

    if (!questionnaireField) {
        return null;
    }

    let fieldValue = questionnaireField.value;

    if (questionnaireField.type === FieldType.PICKUP && item) {
        const pickupLocation = questionnaireField.pickupLocation;

        if (pickupLocation) {
            const name = pickupLocation.name;

            const time =
                "arrivalTime" in item && pickupLocation.arrivalOffset
                    ? getFormattedPickupLocationTime(item.arrivalDate, item.arrivalTime, pickupLocation.arrivalOffset)
                    : null;

            return `${name}${time ? ` (Pickup at ${time})` : ""}`;
        }
    }

    if (isAddonFieldResponse(questionnaireField)) {
        fieldValue = `${fieldValue} ${formatFieldLabel(questionnaireField)}`;
    }

    if (questionnaireField.type === FieldType.CHECKBOX) {
        return fieldValue === "1" ? "Yes" : "No";
    }

    if (fieldValue && questionnaireField.unit) {
        fieldValue = `${fieldValue} ${questionnaireField.unit}`;
    }

    return fieldValue ?? "";
};
